import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ArrowLeft, Send, AlertTriangle, Loader2, Bot, User,
  PhoneCall, ChevronRight
} from "lucide-react";
import { API } from "@/App";

// Helper to clean markdown formatting from AI response
const cleanMarkdown = (text) => {
  if (!text) return "";
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')  // Remove **bold**
    .replace(/\*(.*?)\*/g, '$1')       // Remove *italic*
    .replace(/#{1,6}\s/g, '')          // Remove headers
    .replace(/`{1,3}(.*?)`{1,3}/g, '$1') // Remove code blocks
    .trim();
};

export const IssueChatPage = () => {
  const { issueId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem("token");

  const [issue, setIssue] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [quickFixes, setQuickFixes] = useState([]);

  useEffect(() => {
    fetchIssueAndChat();
  }, [issueId]);

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchIssueAndChat = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [issueRes, chatRes] = await Promise.all([
        axios.get(`${API}/issues/${issueId}`, { headers }),
        axios.get(`${API}/chat/history/${issueId}`, { headers })
      ]);

      setIssue(issueRes.data);
      const history = chatRes.data.chat_history || [];
      setChatHistory(history);

      // If no chat history, send initial greeting
      if (history.length === 0) {
        sendInitialMessage(issueRes.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load chat");
    } finally {
      setLoading(false);
    }
  };

  const sendInitialMessage = async (issueData) => {
    setSending(true);
    try {
      const initialMsg = `Hi, I'm having a ${issueData.issue_type.toLowerCase()} issue. ${issueData.description}`;
      const response = await axios.post(
        `${API}/chat/triage`,
        { issue_id: issueId, message: initialMsg },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Fetch fresh chat history from backend to avoid duplicates
      const chatRes = await axios.get(`${API}/chat/history/${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatHistory(chatRes.data.chat_history || []);

      if (response.data.quick_fixes) {
        setQuickFixes(response.data.quick_fixes);
      }
    } catch (error) {
      console.error("Initial message error:", error);
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || sending) return;

    // Add user message to UI immediately
    const userMsg = {
      role: "user",
      content: text,
      timestamp: new Date().toISOString()
    };
    setChatHistory(prev => [...prev, userMsg]);
    setMessage("");
    setSending(true);

    try {
      const response = await axios.post(
        `${API}/chat/triage`,
        { issue_id: issueId, message: text },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Add cleaned assistant response
      setChatHistory(prev => [...prev, {
        role: "assistant",
        content: cleanMarkdown(response.data.response),
        timestamp: new Date().toISOString(),
        is_safety_alert: response.data.is_safety_alert
      }]);

      if (response.data.quick_fixes) {
        setQuickFixes(response.data.quick_fixes);
      }

      if (response.data.auto_escalated) {
        toast.warning("This issue has been automatically escalated due to safety concerns");
      }
    } catch (error) {
      toast.error("Failed to send message");
      // Remove the user message if sending failed
      setChatHistory(prev => prev.filter(m => m !== userMsg));
    } finally {
      setSending(false);
    }
  };

  const handleEscalate = async () => {
    try {
      const response = await axios.post(
        `${API}/issues/${issueId}/escalate`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success("Issue escalated to a contractor");
      navigate(`/tenant/job/${response.data.job_id}`);
    } catch (error) {
      toast.error("Failed to escalate issue");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/tenant")}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Avatar className="w-10 h-10">
              <AvatarImage src="https://images.unsplash.com/photo-1758598306913-5cd682b9e53b?w=100&h=100&fit=crop&crop=face" />
              <AvatarFallback><Bot className="w-5 h-5" /></AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h1 className="font-semibold">Alex</h1>
              <p className="text-xs text-muted-foreground">Maintenance Assistant</p>
            </div>
          </div>
        </div>
      </header>

      {/* Issue status banner */}
      {issue?.is_emergency && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2">
          <div className="max-w-lg mx-auto flex items-center gap-2 text-red-700 text-sm">
            <AlertTriangle className="w-4 h-4" />
            <span>Emergency issue - priority handling</span>
          </div>
        </div>
      )}

      {/* Chat messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-lg mx-auto space-y-4">
          {chatHistory.map((msg, index) => (
            <div
              key={index}
              className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} animate-slide-in`}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {msg.role === "assistant" && (
                <Avatar className="w-8 h-8 mr-2 shrink-0">
                  <AvatarImage src="https://images.unsplash.com/photo-1758598306913-5cd682b9e53b?w=100&h=100&fit=crop&crop=face" />
                  <AvatarFallback><Bot className="w-4 h-4" /></AvatarFallback>
                </Avatar>
              )}
              <div
                className={msg.role === "user" ? "chat-bubble-user" : `chat-bubble-assistant ${msg.is_safety_alert ? "safety-alert" : ""}`}
                data-testid={`chat-message-${index}`}
              >
                {msg.is_safety_alert && (
                  <div className="flex items-center gap-2 mb-2 text-red-600 font-medium">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Safety Alert</span>
                  </div>
                )}
                <p className="whitespace-pre-wrap">{cleanMarkdown(msg.content)}</p>
              </div>
              {msg.role === "user" && (
                <Avatar className="w-8 h-8 ml-2 shrink-0">
                  <AvatarFallback><User className="w-4 h-4" /></AvatarFallback>
                </Avatar>
              )}
            </div>
          ))}

          {sending && (
            <div className="flex justify-start animate-slide-in">
              <Avatar className="w-8 h-8 mr-2">
                <AvatarFallback><Bot className="w-4 h-4" /></AvatarFallback>
              </Avatar>
              <div className="chat-bubble-assistant">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Escalate button */}
      <div className="bg-white border-t border-slate-200 px-4 py-3">
        <div className="max-w-lg mx-auto">
          <Card
            className="cursor-pointer hover:bg-slate-50 transition-colors mb-3"
            onClick={handleEscalate}
            data-testid="escalate-btn"
          >
            <CardContent className="p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <PhoneCall className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-sm">Need a contractor?</p>
                <p className="text-xs text-muted-foreground">Escalate this issue for professional help</p>
              </div>
              <ChevronRight className="w-5 h-5 text-slate-400" />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-slate-200 px-4 py-3 pb-safe">
        <div className="max-w-lg mx-auto flex gap-2">
          <Input
            data-testid="chat-input"
            placeholder="Type your message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            className="flex-1 h-12 rounded-full px-5"
            disabled={sending}
          />
          <Button
            data-testid="send-btn"
            size="icon"
            className="w-12 h-12 rounded-full"
            onClick={() => sendMessage(message)}
            disabled={!message.trim() || sending}
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>
    </div>
  );
};
